import { useId, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useNavigate } from "react-router";
import { generatePassword } from "@passwordflow/crypto";
import { Dices, Keyboard, KeyRound, LayoutDashboard, Lock, Plus, Settings, ShieldHalf, Star, Trash2, Users, Wand2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useSecretCopy } from "../app/clipboard.js";
import { useMe } from "../data/account.js";
import { useLockVault } from "../app/lockVault.js";
import { paths } from "../app/paths.js";
import { useVaultEntries } from "../data/vault.js";
import type { Entry } from "../lib/entries.js";
import { matchesQuery } from "../lib/entries.js";
import { useEntryActions } from "../features/vault/useEntryActions.js";
import { Dialog } from "../ui/Dialog.js";
import { Avatar, Kbd } from "../ui/primitives.js";
import { TextInput } from "../ui/forms.js";

interface PaletteItem {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly icon: LucideIcon;
  readonly run: () => void;
  readonly entry?: Entry | undefined;
}

export function CommandPalette(props: { readonly open: boolean; readonly onClose: () => void; readonly onShowShortcuts: () => void }): ReactNode {
  return (
    <Dialog open={props.open} onClose={props.onClose} title="Suchen oder Befehl ausführen" variant="top" size="md">
      <PaletteBody onClose={props.onClose} onShowShortcuts={props.onShowShortcuts} />
    </Dialog>
  );
}

function PaletteBody(props: { readonly onClose: () => void; readonly onShowShortcuts: () => void }): ReactNode {
  const navigate = useNavigate();
  const me = useMe();
  const lock = useLockVault();
  const entries = useVaultEntries();
  const actions = useEntryActions();
  const copy = useSecretCopy();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  function go(path: string): void {
    props.onClose();
    navigate(path);
  }

  const commands: PaletteItem[] = [
    { id: "new", label: "Neuer Eintrag", hint: "Aktion", icon: Plus, run: (): void => go(paths.vaultNew) },
    {
      id: "gen-copy",
      label: "Starkes Passwort erzeugen und kopieren",
      hint: "Aktion",
      icon: Dices,
      run: (): void => {
        props.onClose();
        void copy(generatePassword({ length: 24, sets: ["lower", "upper", "digits", "symbols"] }), "Neues Passwort kopiert");
      }
    },
    { id: "gen", label: "Passwort-Generator", hint: "Werkzeug", icon: Wand2, run: (): void => go(paths.generator) },
    { id: "dash", label: "Übersicht", hint: "Seite", icon: LayoutDashboard, run: (): void => go(paths.dashboard) },
    { id: "all", label: "Alle Einträge", hint: "Seite", icon: KeyRound, run: (): void => go(paths.vault) },
    { id: "fav", label: "Favoriten", hint: "Seite", icon: Star, run: (): void => go("/vault?view=favorites") },
    { id: "trash", label: "Papierkorb", hint: "Seite", icon: Trash2, run: (): void => go("/vault?view=trash") },
    { id: "teams", label: "Teams", hint: "Seite", icon: Users, run: (): void => go(paths.teams) },
    { id: "settings", label: "Einstellungen und Sicherheit", hint: "Seite", icon: Settings, run: (): void => go(paths.settings) },
    ...(me.data?.role === "admin"
      ? [{ id: "admin", label: "Verwaltung: Konten", hint: "Seite", icon: ShieldHalf, run: (): void => go(paths.admin) }]
      : []),
    { id: "keys", label: "Tastenkürzel anzeigen", hint: "Hilfe", icon: Keyboard, run: props.onShowShortcuts },
    {
      id: "lock",
      label: "Tresor sperren",
      hint: "Aktion",
      icon: Lock,
      run: (): void => {
        props.onClose();
        void lock("manual");
      }
    }
  ];

  const needle = query.trim();
  const matchingEntries: Entry[] = (entries.data?.entries ?? []).filter((entry: Entry): boolean => needle !== "" && matchesQuery(entry, needle)).slice(0, 8);
  const entryItems: PaletteItem[] = matchingEntries.map((entry: Entry): PaletteItem => ({
    id: `entry-${entry.meta.id}`,
    label: entry.data.title,
    hint: entry.data.username !== "" ? entry.data.username : entry.meta.folder,
    icon: KeyRound,
    run: (): void => go(paths.vaultItem(entry.meta.id)),
    entry
  }));

  const visible: PaletteItem[] = [
    ...commands.filter((c: PaletteItem): boolean => needle === "" || c.label.toLowerCase().includes(needle.toLowerCase())),
    ...entryItems
  ];
  const index = visible.length === 0 ? 0 : Math.min(active, visible.length - 1);

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (visible.length === 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index + 1) % visible.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index - 1 + visible.length) % visible.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = visible[index];
      if (item?.entry !== undefined && (event.shiftKey || event.altKey)) {
        props.onClose();
        if (event.shiftKey) {
          actions.copyPassword(item.entry);
        } else {
          actions.copyUsername(item.entry);
        }
        return;
      }
      item?.run();
    }
  }

  return (
    <div className="flex flex-col">
      <div className="border-b border-line p-3">
        <TextInput
          data-autofocus
          role="combobox"
          aria-expanded={true}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={visible[index] !== undefined ? `${listId}-${index}` : undefined}
          value={query}
          onChange={(event): void => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Eintrag, Seite oder Befehl suchen …"
          aria-label="Suchen oder Befehl ausführen"
          autoComplete="off"
          className="h-10 border-transparent bg-ink focus:ring-0"
        />
      </div>
      <ul id={listId} role="listbox" aria-label="Ergebnisse" className="max-h-[52vh] overflow-y-auto p-1.5">
        {visible.length === 0 ? (
          <li className="px-4 py-10 text-center text-sm text-mist">Keine Treffer für „{needle}“.</li>
        ) : null}
        {visible.map((item: PaletteItem, position: number): ReactNode => {
          const Icon = item.icon;
          const selected = position === index;
          return (
            <li
              key={item.id}
              id={`${listId}-${position}`}
              role="option"
              aria-selected={selected}
              onMouseEnter={(): void => setActive(position)}
              onClick={(): void => item.run()}
              className={`flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 transition-colors ${
                selected ? "bg-foam/[0.07] text-foam" : "text-mist"
              }`}
            >
              {item.entry !== undefined ? (
                <Avatar label={item.label} size="sm" />
              ) : (
                <span className="grid size-7 shrink-0 place-items-center rounded-md border border-line bg-ink-3 text-flow">
                  <Icon size={14} aria-hidden="true" />
                </span>
              )}
              <span className="min-w-0 flex-1 truncate text-sm">{item.label}</span>
              <span className="shrink-0 text-xs text-mist-2">{item.hint}</span>
            </li>
          );
        })}
      </ul>
      <div aria-hidden="true" className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-4 py-2.5 text-xs text-mist-2">
        <span className="inline-flex items-center gap-1.5"><Kbd>↵</Kbd> öffnen</span>
        <span className="inline-flex items-center gap-1.5"><Kbd>⇧</Kbd><Kbd>↵</Kbd> Passwort kopieren</span>
        <span className="inline-flex items-center gap-1.5"><Kbd>Alt</Kbd><Kbd>↵</Kbd> Benutzer kopieren</span>
      </div>
    </div>
  );
}
